// InteliChat #41 - sound layer (AudioContext, agent-controlled).
//
// WHY its own player (not the native DashboardAudioNotificationHelper's
// `new Audio()` nor the widget's window.playAudioAlert): the dashboard
// never wires window.playAudioAlert (that lives in the widget SDK only),
// and we need first-class control the native path doesn't give us -
// per-agent volume, tone choice, an explicit autoplay-unlock primer, and
// a repeat-until-answered LOOP for an unanswered NEW/incoming chat. We
// reuse the native tone assets (/audio/dashboard/{bell,chime,ding,
// drumroll,magic}.mp3) and the same AudioContext + decodeAudioData
// pattern as shared/helpers/AudioNotificationHelper.getAlertAudio.
//
// Autoplay policy: a WebSocket-triggered play() is blocked until the page
// audio has been unlocked by a real user gesture. primeOnGesture() (called
// once from the engine on the agent's first click/keydown) resumes the
// context and plays a silent buffer so all later event-driven plays work.

export const TONES = ['ding', 'bell', 'chime', 'drumroll', 'magic'];
const TONE_BASE = '/audio/dashboard/';
const REPEAT_MS = 4000; // gap between repeat-until-answered pulses
const MAX_REPEATS = 30; // safety cap (~2 min) so a forgotten loop can't run forever

let ctx = null;
const buffers = {}; // tone -> decoded AudioBuffer
let unlocked = false;
let loopTimer = null;
let loopCount = 0;

// ---- context + buffer management ------------------------------------
const getCtx = () => {
  if (ctx) return ctx;
  try {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
  } catch {
    ctx = null;
  }
  return ctx;
};

const loadTone = async tone => {
  const t = TONES.includes(tone) ? tone : 'ding';
  if (buffers[t]) return buffers[t];
  const c = getCtx();
  if (!c) return null;
  try {
    const res = await fetch(`${TONE_BASE}${t}.mp3`);
    const arr = await res.arrayBuffer();
    const buf = await c.decodeAudioData(arr);
    buffers[t] = buf;
    return buf;
  } catch {
    return null;
  }
};

// ---- unlock primer (call once on the agent's first gesture) ---------
export const isUnlocked = () => unlocked;

export const primeOnGesture = async () => {
  const c = getCtx();
  if (!c) return false;
  try {
    if (c.state === 'suspended') await c.resume();
    // silent play-pause so the context is genuinely user-activated
    const src = c.createBufferSource();
    src.buffer = c.createBuffer(1, 1, 22050);
    src.connect(c.destination);
    src.start(0);
    unlocked = c.state === 'running';
    // warm the default tone so the first real alert has zero latency
    loadTone('ding');
  } catch {
    unlocked = false;
  }
  return unlocked;
};

// ---- one-shot play --------------------------------------------------
// Returns true if it actually played (context running + buffer ready).
export const playTone = async ({ tone = 'ding', volume = 0.8 } = {}) => {
  const c = getCtx();
  if (!c) return false;
  if (c.state === 'suspended') {
    // still locked - the engine surfaces the "click to enable sound" hint
    try {
      await c.resume();
    } catch {
      return false;
    }
  }
  unlocked = c.state === 'running';
  if (!unlocked) return false;
  const buf = await loadTone(tone);
  if (!buf) return false;
  try {
    const src = c.createBufferSource();
    src.buffer = buf;
    const gain = c.createGain();
    gain.gain.value = Math.max(0, Math.min(1, Number(volume)));
    src.connect(gain);
    gain.connect(c.destination);
    src.loop = false;
    src.start(0);
    return true;
  } catch {
    return false;
  }
};

// ---- repeat-until-answered loop (for an unanswered NEW/incoming chat) -
export const startRepeat = ({ tone = 'ding', volume = 0.8 } = {}) => {
  stopRepeat();
  loopCount = 0;
  // fire immediately, then on an interval until stopRepeat() (answered /
  // opened / focus-suppressed) or the safety cap.
  const pulse = () => {
    if (loopCount >= MAX_REPEATS) {
      stopRepeat();
      return;
    }
    loopCount += 1;
    playTone({ tone, volume });
  };
  pulse();
  loopTimer = setInterval(pulse, REPEAT_MS);
};

export const stopRepeat = () => {
  if (loopTimer) {
    clearInterval(loopTimer);
    loopTimer = null;
  }
  loopCount = 0;
};

export const isRepeating = () => loopTimer !== null;

// Context may be suspended even after a prior unlock (tab backgrounded,
// device sleep). The engine uses this to decide whether to show the
// "click to enable sound" banner.
export const contextSuspended = () => {
  const c = getCtx();
  return !c || c.state === 'suspended';
};
